/* eslint-disable @typescript-eslint/no-explicit-any */
import * as History from 'history';
import { MobileNativeContext } from 'providers/withApplicationContext';
import { Network, OAuthProfile, loginWith } from 'components/OAuth';
import { Route, RouteComponentProps } from 'react-router-dom';
import { capitalize } from 'lodash';
import { useFeatureFlag } from '@tock/shared/utils/featureFlagContext';
import OAuthCheckRoute from 'routes/unprotected/OAuthCheckRoute';
import React from 'react';
import SocialLoginButton, { SupportedSocialLogin } from 'components/SocialLoginButton';

interface SocialLoginButtonConfig {
  network: SupportedSocialLogin;
  onClick: () => void;
}

type ActionType = 'Sign-up' | 'Log-in';

interface Props {
  action: ActionType;
  continuePath: string;
  location: History.Location;
  onSocialSuccess(network: Network, accessToken: string, profile: OAuthProfile): void;
  onSocialFailure(err: { error: string | object }): Promise<any> | void;
}

const SocialLoginButtons = ({
  action,
  continuePath,
  location,
  onSocialSuccess,
  onSocialFailure,
}: Props) => {
  const hideFbLogin = useFeatureFlag('HIDE_FB_LOGIN');
  const { mobileNative, reactNative } = React.useContext(MobileNativeContext);
  // only allow sign in w/ Apple if ff is enabled and we're either in the react native app or on the web
  const useSignInWithApple = reactNative || !mobileNative;

  const applyAnalyticsReferrerFix = useFeatureFlag('SOCIAL_LOGIN_ANALYTICS_REFERRER_FIX');

  const connectWithFacebook = () => {
    return new Promise<void>((resolve, reject) => {
      try {
        loginWith('facebook', location.pathname, continuePath, applyAnalyticsReferrerFix);
      } catch (err: any) {
        reject({ _error: `Error connectWithFacebook ${err?.message}` });
      }
      resolve();
    });
  };

  const connectWithGoogle = () => {
    loginWith('google', location.pathname, continuePath, applyAnalyticsReferrerFix);
  };

  const connectWithApple = () => {
    loginWith('apple', location.pathname, continuePath, applyAnalyticsReferrerFix);
  };

  const getButtonText = (socialNetwork: string) => {
    switch (action) {
      case 'Sign-up':
        return `Use my ${socialNetwork} account`;
      case 'Log-in':
        return `Log in with ${socialNetwork}`;
      default:
        return '';
    }
  };

  const fbLogin: SocialLoginButtonConfig = {
    network: 'facebook',
    onClick: connectWithFacebook,
  };
  const appleLogin: SocialLoginButtonConfig = {
    network: 'apple' as SupportedSocialLogin,
    onClick: connectWithApple,
  };

  const buttonConfigs: SocialLoginButtonConfig[] = [
    ...(hideFbLogin ? [] : [fbLogin]),
    {
      network: 'google',
      onClick: connectWithGoogle,
    },
    ...(useSignInWithApple ? [appleLogin] : []),
  ];

  const renderOAuthCheckRoute = (routeProps: RouteComponentProps) => {
    return (
      <OAuthCheckRoute {...routeProps} onSuccess={onSocialSuccess} onFailure={onSocialFailure} />
    );
  };

  return (
    <>
      {buttonConfigs.map(({ network, onClick }) => {
        const displayName = capitalize(network);

        return (
          <SocialLoginButton
            analyticsEventLabel={displayName}
            network={network}
            text={getButtonText(displayName)}
            onClick={onClick}
            key={network}
          />
        );
      })}
      <Route path={['/', '/oauth-check']} render={renderOAuthCheckRoute} />
    </>
  );
};

export default SocialLoginButtons;
